import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../../../config";
import {
  getPeriodRanges,
  summarizeNewCustomers,
} from "./newCustomersSecuredHelpers";
import "./NewCustomersSecured.css";

// "New Customers Secured" widget — counts of newly secured customers (distinct
// newly-appearing Costar Property Ids) for Week / Month-To-Date / Year-To-Date.
//
// Role behaviour is driven by the same dataScope contract every dashboard widget
// uses (no role names hardcoded):
//   dataScope === "self" (Sales): only the logged-in user's data — Week / MTD /
//     YTD shown as KPI cards.
//   dataScope === "all" (Sales Director / President): overall MTD + YTD totals,
//     then a per-sales-representative breakdown (Rep | MTD | YTD).
//
// The newly-secured determination is computed client-side from GetCustomerInfo
// (`costarId` + `createdOn`); see newCustomersSecuredHelpers. A single
// year-to-date fetch feeds all three windows.

// Fetch every GetCustomerInfo record in the year-to-date window, paging through
// TotalCount. Capped so a bad/huge response can never loop unbounded.
const PAGE_SIZE = 200;
const MAX_PAGES = 50;

const fetchAllNewCustomerRecords = async ({ token, userId, salesPersonId }) => {
  const { year } = getPeriodRanges();
  const all = [];
  let pageIndex = 1;
  let total = Infinity;
  while (all.length < total && pageIndex <= MAX_PAGES) {
    const res = await axios.get(
      `${API_URL}/User/api/CustomerInfo/GetCustomerInfo`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          PageIndex: pageIndex,
          PageSize: PAGE_SIZE,
          userId,
          sort: "asc",
          FromDate: year.fromDate,
          ToDate: year.toDate,
          ...(salesPersonId ? { SalesPersonId: salesPersonId } : {}),
        },
      }
    );
    const data = res && res.data ? res.data : {};
    const rows = Array.isArray(data.result) ? data.result : [];
    all.push(...rows);
    total = Number.isFinite(data.TotalCount) ? data.TotalCount : all.length;
    if (rows.length === 0) break; // defensive: no progress
    pageIndex += 1;
  }
  return all;
};

const NewCustomersSecured = ({ auth: { auth }, dataScope = "self", userId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const token = auth && auth[0] ? auth[0].token : null;
  const tmpSalesId = auth && auth[0] ? auth[0].salesPersonId : null;
  const isSelf = dataScope === "self";
  const salesPersonId = isSelf ? tmpSalesId : null;
// 
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchAllNewCustomerRecords({ token, userId, salesPersonId })
      .then((records) => {
        if (cancelled) return;
        setSummary(summarizeNewCustomers(records));
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("Could not load New Customers Secured:", err.message);
        setError(true);
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, userId, salesPersonId]);

  const totals = summary ? summary.totals : { week: 0, mtd: 0, ytd: 0 };
  const reps = summary ? summary.reps : [];

  // Sales (self) scopes the listing to the logged-in user; Director/President
  // ("all") drill into every rep's customers for the window.
  const scopeExtra = isSelf ? { salesPersonId: userId } : {};

  const ranges = getPeriodRanges();

  const openListing = (range, extra = {}) => {
    const params = new URLSearchParams({
      fromDate: range.fromDate,
      toDate: range.toDate,
      salesPersonId: salesPersonId
    });
    // Object.entries(extra).forEach(([k, v]) => {
    //   if (v) params.set(k, v);
    // });
    navigate(`/new-customers-secured-info?${params.toString()}`);
  };

  // A KPI card; only clickable (drills into the listing) when its count > 0.
  const renderCard = (label, count, range, extra) => {
    const clickable = count > 0;
    return (
      <div
        className={`ncs-card${clickable ? " ncs-clickable" : ""}`}
        onClick={clickable ? () => openListing(range, extra) : undefined}
      >
        <div className="ncs-card-value">{count}</div>
        <div className="ncs-card-label">{label}</div>
      </div>
    );
  };


  return (
    <div className="ncs-widget" style={{'minWidth': '660px'}}>
      <h4 className="ncs-title">New Customers Secured</h4>

      {loading ? (
        <div className="ncs-state">Loading…</div>
      ) : error ? (
        <div className="ncs-state ncs-error">Unable to load data.</div>
      ) : isSelf ? (
        // Sales: Week / MTD / YTD as KPI cards.
        <div className="ncs-cards">
          {renderCard("Week To Date", totals.week, ranges.week, scopeExtra)}
          {renderCard("Month To Date", totals.mtd, ranges.month, scopeExtra)}
          {renderCard("Year To Date", totals.ytd, ranges.year, scopeExtra)}
        </div>
      ) : (
        // Sales Director / President: overall totals + per-rep breakdown.
        <div className="ncs-overview">
          <div className="ncs-cards">
            {renderCard("Month To Date", totals.mtd, ranges.month, scopeExtra)}
            {renderCard("Year To Date", totals.ytd, ranges.year, scopeExtra)}
          </div>

          <table className="ncs-table">
            <thead>
              <tr>
                <th className="ncs-rowhead">Sales Person</th>
                <th>Month To Date</th>
                <th>Year To Date</th>
              </tr>
            </thead>
            <tbody>
              {reps.length > 0 ? (
                reps.map((rep) => (
                  <tr key={rep.repName}>
                    <td className="ncs-rep">{rep.repName}</td>
                    <td className="ncs-num">{rep.mtd}</td>
                    <td className="ncs-num">{rep.ytd}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="ncs-empty">
                    No new customers secured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export { NewCustomersSecured };

export default connect(mapStateToProps)(NewCustomersSecured);
